/**
 * Koffer-Status-Seite
 *
 * Zeigt von jedem Koffer den letzten Systemcheck (Health-Check) an.
 * Online = letzte Nachricht des Koffers ist neuer als 5 Minuten.
 *
 * @module KofferStatus
 */

import React, { Component } from 'react';
import SecuredZone from '../components/SecuredZone';
import API from '../api/api';
import LinearProgress from '@mui/material/LinearProgress';
import { Link } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimerIcon from '@mui/icons-material/Timer';
import StorageIcon from '@mui/icons-material/Storage';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SdCardIcon from '@mui/icons-material/SdCard';
import SensorsIcon from '@mui/icons-material/Sensors';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import RouterIcon from '@mui/icons-material/Router';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import LinkIcon from '@mui/icons-material/Link';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import CompressIcon from '@mui/icons-material/Compress';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AssignmentIcon from '@mui/icons-material/Assignment';
import moment from 'moment';
import { DEVICE_CONSTANTS } from '../utils/deviceConstants';

/** Einfache Icons pro Status-Typ (eine Icon-Komponente pro Feld). */
const STATUS_ICONS = {
  signal: SignalCellularAltIcon,
  uptime: TimerIcon,
  memory: StorageIcon,
  sd: SdCardIcon,
  sensor: SensorsIcon,
  modem: PhoneAndroidIcon,
  netz: NetworkCheckIcon,
  daten: CloudQueueIcon,
  server: RouterIcon,
  reset: RestartAltIcon,
  checks: AssignmentIcon,
  ip: RouterIcon,
  connect: LinkIcon,
  temperatur: ThermostatIcon,
  druck: CompressIcon,
  feuchtigkeit: WaterDropIcon,
  gas: LocalGasStationIcon,
};

const FireApi = new API();

const ONLINE_THRESHOLD_MS = DEVICE_CONSTANTS.OFFLINE_THRESHOLD_MINUTES * 60 * 1000;

function formatUptime(ms) {
  if (ms === undefined || ms === null) return '–';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

function formatMemory(bytes) {
  if (bytes === undefined || bytes === null) return '–';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} B`;
}

function getResetCauseLabel(value) {
  const labels = {
    WDT: 'Programm hing oder war blockiert',
    BOR: 'Spannung war zu niedrig',
    EXT: 'Externer Reset (z. B. Reset-Taste)',
    POR: 'Gerät wurde eingeschaltet',
    UNK: 'Unbekannt',
  };
  return value ? (labels[value] || value) : value;
}

function formatSignalQuality(csq) {
  if (csq === undefined || csq === null) return '–';
  if (csq === -1) return 'unbekannt';
  if (csq < 0 || csq > 31) return `${csq}`;
  const hint = csq < 10 ? ' (schlecht)' : csq <= 20 ? ' (ok)' : ' (gut)';
  return `${csq}${hint}`;
}

/** Variante (Farbe) aus CSQ. */
function getSignalVariant(csq) {
  if (csq === undefined || csq === null || csq === -1 || csq < 10) return 'error';
  if (csq <= 20) return 'warning';
  return 'success';
}

/** Neustart-Grund: Variante für 1-Block-Indikator (success/error/neutral). */
function getResetCauseVariant(cause) {
  if (!cause) return 'neutral';
  if (cause === 'POR' || cause === 'EXT') return 'success';
  if (cause === 'WDT' || cause === 'BOR') return 'error';
  return 'neutral';
}

/** Icons und Varianten für Zähler (0 = gut, niedrig = Warnung, hoch = Fehler). */
function getCountIconAndVariant(value) {
  if (value === 0) return { Icon: CheckCircleIcon, variant: 'success' };
  if (value < 5) return { Icon: WarningAmberIcon, variant: 'warning' };
  return { Icon: ErrorIcon, variant: 'error' };
}

class DevicesStatus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      devices: [],
      deviceStatuses: {}, // deviceId -> { lastLog, lastHealthCheck, isOnline }
      loading: true,
    };
  }

  loadDevices() {
    this.setState({ loading: true });
    const config = { withCredentials: true, crossDomain: true };
    const pagination = { page: 1, page_size: 500 };

    FireApi.endpoints.devicesStatus.getAll(config, pagination)
      .then((devicesRes) => {
        let list = [];
        if (devicesRes?.data?.results && Array.isArray(devicesRes.data.results)) {
          list = devicesRes.data.results;
        } else if (Array.isArray(devicesRes?.data)) {
          list = devicesRes.data;
        }
        list = list.filter((p) => p && p.id);

        const deviceStatuses = {};
        list.forEach((device) => {
          const lastCreated = device.value_created ? new Date(device.value_created).getTime() : 0;
          deviceStatuses[device.id] = {
            deviceId: device.id,
            lastHealthCheck: {
              created: device.value_created,
              data: {
                temperature: device.value_t,
                humidity: device.value_h,
                pressure: device.value_p,
                gas: device.value_g,
                signalQuality: device.value_s,
              },
            },
            isOnline: lastCreated > 0 && Date.now() - lastCreated < ONLINE_THRESHOLD_MS,
          };
        });

        this.setState({ devices: list, deviceStatuses, loading: false });
      })
      .catch(() => this.setState({ devices: [], deviceStatuses: {}, loading: false }));
  }

  componentDidMount() {
    this.loadDevices();
  }

  /** Einfaches Icon + Label + Wert (eine Icon-Komponente pro Typ, Farbe nach Variante). */
  renderSimpleIconItem(label, value, type, variant = 'neutral', fullWidth = false) {
    const Icon = STATUS_ICONS[type] || CheckCircleIcon;
    return (
      <div key={label} className={`panel-status-data-item ${variant} ${fullWidth ? 'full-width' : ''}`}>
        <div className={`panel-status-data-icon ${variant}`}>
          <Icon style={{ fontSize: 22 }} />
        </div>
        <div className="panel-status-data-content">
          <span className="panel-status-data-label">{label}</span>
          <span className="panel-status-data-value">{value}</span>
        </div>
      </div>
    );
  }

  render() {
    const { devices, deviceStatuses, loading } = this.state;

    const onlineCount = devices.filter((p) => deviceStatuses[p.id]?.isOnline).length;
    const offlineCount = devices.length - onlineCount;
    const onlineDevices = devices.filter((p) => deviceStatuses[p.id]?.isOnline);
    const offlineDevices = devices.filter((p) => !deviceStatuses[p.id]?.isOnline);

    const renderCard = (device) => {
      const status = deviceStatuses[device.id] || {};
      const { lastHealthCheck, isOnline } = status;
      const data = lastHealthCheck?.log?.data || lastHealthCheck?.data || {};
      const created = lastHealthCheck?.created;

      return (
                <Card key={device.id} className={`panel-status-card ${isOnline ? 'online' : 'offline'}`} elevation={0}>
                  <div className="panel-status-card-header">
                    <Typography variant="h6" className="panel-status-card-title">
                      {device.name || `Koffer ${device.id}`}
                    </Typography>
                    <div className="panel-status-card-actions">
                      <span className={`panel-status-badge ${isOnline ? 'online' : 'offline'}`}>
                        {isOnline ? <CheckCircleIcon style={{ fontSize: '18px' }} /> : <ErrorIcon style={{ fontSize: '18px' }} />}
                        {isOnline ? 'Online' : 'Offline'}
                      </span>
                      <Button
                        size="small"
                        variant="outlined"
                        component={Link}
                        to={`/devices/${device.id}`}
                        endIcon={<OpenInNewIcon />}
                        style={{ fontSize: '12px', textTransform: 'none' }}
                      >
                        Öffnen
                      </Button>
                    </div>
                  </div>

                  <CardContent className="panel-status-card-body">
                    {lastHealthCheck ? (
                      <>
                        <div className="panel-status-last-check">
                          <HealthAndSafetyIcon />
                          <span>Letzter Systemcheck: {created ? moment(created).format('DD.MM.YYYY HH:mm') : '–'}</span>
                        </div>

                        <div className="panel-status-section">
                          <div className="panel-status-section-title">System</div>
                          <div className="panel-status-data-grid">
                            {data.uptime !== undefined && this.renderSimpleIconItem('Laufzeit', formatUptime(data.uptime), 'uptime', 'success')}
                            {data.freeMem !== undefined && this.renderSimpleIconItem('Freier Speicher', formatMemory(data.freeMem), 'memory', 'neutral')}
                            {data.resetCause && this.renderSimpleIconItem('Neustart', getResetCauseLabel(data.resetCause), 'reset', getResetCauseVariant(data.resetCause))}
                            {data.wdtCount !== undefined && this.renderSimpleIconItem('Checks', String(data.wdtCount), 'checks', 'neutral')}
                          </div>
                        </div>

                        <div className="panel-status-section">
                          <div className="panel-status-section-title">Verbindung & Komponenten</div>
                          <div className="panel-status-data-grid">
                            {data.sdOK !== undefined && this.renderSimpleIconItem('SD-Karte', data.sdOK ? 'OK' : 'Fehler', 'sd', data.sdOK ? 'success' : 'error')}
                            {data.bmeOK !== undefined && this.renderSimpleIconItem('Sensor', data.bmeOK ? 'OK' : 'Fehler', 'sensor', data.bmeOK ? 'success' : 'error')}
                            {data.gsmReady !== undefined && this.renderSimpleIconItem('Modem', data.gsmReady ? 'Ja' : 'Nein', 'modem', data.gsmReady ? 'success' : 'error')}
                            {data.gsmNetwork !== undefined && this.renderSimpleIconItem('Netz', data.gsmNetwork ? 'Ja' : 'Nein', 'netz', data.gsmNetwork ? 'success' : 'error')}
                            {data.gsmGprs !== undefined && this.renderSimpleIconItem('Daten', data.gsmGprs ? 'Verbunden' : 'Nein', 'daten', data.gsmGprs ? 'success' : 'error')}
                            {data.mqttConnected !== undefined && this.renderSimpleIconItem('Server', data.mqttConnected ? 'Verbunden' : 'Nein', 'server', data.mqttConnected ? 'success' : 'error')}
                            {data.ipAddress !== undefined && data.ipAddress !== null && this.renderSimpleIconItem('IP', String(data.ipAddress), 'ip', 'success')}
                            {data.signalQuality !== undefined && this.renderSimpleIconItem('Empfang', formatSignalQuality(data.signalQuality), 'signal', getSignalVariant(data.signalQuality))}
                            {data.lastConnSuccess !== undefined && this.renderSimpleIconItem('Connect', formatUptime(data.lastConnSuccess), 'connect', 'success')}
                          </div>
                        </div>

                        <div className="panel-status-section">
                          <div className="panel-status-section-title">Fehler & Zähler</div>
                          <div className="panel-status-data-grid">
                            {data.gsmFailures !== undefined && this.renderSimpleIconItem('Modem-Fehler', String(data.gsmFailures), 'modem', getCountIconAndVariant(data.gsmFailures).variant)}
                            {data.mqttFailures !== undefined && this.renderSimpleIconItem('Server-Fehler', String(data.mqttFailures), 'server', getCountIconAndVariant(data.mqttFailures).variant)}
                            {data.retryCount !== undefined && this.renderSimpleIconItem('Reconnect', String(data.retryCount), 'connect', getCountIconAndVariant(data.retryCount).variant)}
                          </div>
                        </div>

                        <div className="panel-status-section">
                          <div className="panel-status-section-title">Umgebung</div>
                          <div className="panel-status-data-grid">
                            {data.temperature !== undefined && data.temperature !== null && this.renderSimpleIconItem('Temperatur', typeof data.temperature === 'number' ? `${data.temperature.toFixed(1)} °C` : data.temperature, 'temperatur', 'neutral')}
                            {data.pressure !== undefined && data.pressure !== null && this.renderSimpleIconItem('Luftdruck', typeof data.pressure === 'number' ? `${data.pressure.toFixed(1)} hPa` : data.pressure, 'druck', 'neutral')}
                            {data.humidity !== undefined && data.humidity !== null && this.renderSimpleIconItem('Feuchtigkeit', typeof data.humidity === 'number' ? `${data.humidity.toFixed(1)} %` : data.humidity, 'feuchtigkeit', 'neutral')}
                            {data.gas !== undefined && data.gas !== null && this.renderSimpleIconItem('Gas', typeof data.gas === 'number' ? `${data.gas.toFixed(1)} kΩ` : data.gas, 'gas', 'neutral')}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="panel-status-empty">
                        Noch kein Systemcheck vorhanden
                      </div>
                    )}
                  </CardContent>
                </Card>
      );
    };

    return (
      <SecuredZone
        title="Status"
        breadcrumbs={{ '/': 'Dashboard', '/devices': 'Koffer', '/devices/status': 'Status' }}
        loading={loading ? <LinearProgress color="primary" /> : null}
      >
        <div className="status-page">
          {!loading && devices.length > 0 && (
            <div className="status-page-summary">
              <div className="status-page-summary-item">
                <span>Koffer</span>
                <strong>{devices.length}</strong>
              </div>
              <div className="status-page-summary-divider" />
              <div className="status-page-summary-item online">
                <CheckCircleIcon style={{ fontSize: '20px' }} />
                <span>Online</span>
                <strong>{onlineCount}</strong>
              </div>
              <div className="status-page-summary-item offline">
                <ErrorIcon style={{ fontSize: '20px' }} />
                <span>Offline</span>
                <strong>{offlineCount}</strong>
              </div>
            </div>
          )}

          {onlineDevices.length > 0 && (
            <>
              <div className="status-page-group-title">Online ({onlineDevices.length})</div>
              <div className="status-page-grid">
                {onlineDevices.map((device) => renderCard(device))}
              </div>
            </>
          )}

          {offlineDevices.length > 0 && (
            <>
              <div className="status-page-group-title">Offline ({offlineDevices.length})</div>
              <div className="status-page-grid">
                {offlineDevices.map((device) => renderCard(device))}
              </div>
            </>
          )}

          {!loading && devices.length === 0 && (
            <div className="status-page-empty">
              <HealthAndSafetyIcon className="status-page-empty-icon" />
              <div className="status-page-empty-text">Keine Koffer vorhanden</div>
            </div>
          )}
        </div>
      </SecuredZone>
    );
  }
}

export default DevicesStatus;
